import { VariableMap } from '@apollo/client/core/LocalState';
import { FragmentMap, isField, isInlineFragment, shouldInclude } from '@apollo/client/utilities';
import { FieldNode, SelectionSetNode } from 'graphql';
import { merge } from 'lodash/fp';

import { GqlOperationInfo } from '@skippay/core/utils/gqlEngineUtils';

const TYPENAME = `__typename`;

// @ts-ignore
export type ObjectSelection = Record<string, ObjectSelection | null>;

class GqlObjectSelection {
	private field: FieldNode;
	private readonly fragmentMap: FragmentMap;
	private readonly variables: VariableMap;

	constructor(info: GqlOperationInfo, variables: VariableMap) {
		this.fragmentMap = info.fragmentMap;
		this.field = info.field;
		this.variables = variables;
	}

	public toObject(): ObjectSelection | undefined {
		return this.field.selectionSet ? this.selectionSetToObject(this.field.selectionSet) : undefined;
	}
	private selectionSetToObject(selectionSet: SelectionSetNode): ObjectSelection {
		return selectionSet.selections.reduce((acc, selection) => {
			if (shouldInclude(selection, this.variables)) {
				if (isField(selection) && selection.name.value !== TYPENAME) {
					const fieldSelectionSet = selection.selectionSet;
					const fieldSelection = !fieldSelectionSet
						? null
						: this.selectionSetToObject(fieldSelectionSet);
					const fieldName = selection.name.value;
					const existingSelection = acc[fieldName];
					acc[fieldName] = existingSelection
						? merge(existingSelection, fieldSelection)
						: fieldSelection;
				} else {
					const fragment = isInlineFragment(selection)
						? selection
						: this.fragmentMap[selection.name.value];
					if (fragment) {
						return merge(acc, this.selectionSetToObject(fragment.selectionSet));
					}
				}
			}
			return acc;
		}, {} as ObjectSelection);
	}
}

const selectionCache = new WeakMap<GqlOperationInfo, WeakMap<VariableMap, ObjectSelection>>();
const emptyVariables = Object.freeze({});

export const getObjectSelection = (
	info: GqlOperationInfo,
	variables: VariableMap = emptyVariables
): ObjectSelection | undefined => {
	let cachedInfo = selectionCache.get(info);
	const cached = cachedInfo?.get(variables);
	if (cached) return cached;
	const objectSelection = new GqlObjectSelection(info, variables).toObject();
	if (!cachedInfo) {
		cachedInfo = new WeakMap<VariableMap, ObjectSelection>();
		selectionCache.set(info, cachedInfo);
	}
	cachedInfo.set(variables, objectSelection);
	return objectSelection;
};
